import { Box, Fab, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { isMobile, isMobileSafari, isTablet, useMobileOrientation } from 'react-device-detect';
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { getParentViewIDFromPath, getRouteFromContext } from "../../common/utilities/RouteHelpers";
import AppBarBottom from "../../components/app-bar-bottom/AppBarBottom";
import AppHeader from "../../components/app-header/AppHeader";
import HelpMenu from "../../components/help-menu/HelpMenu";
import HierarchyDrawer from "../../components/hierarchy-drawer/HierarchyDrawer";
import LoginMessagesDialog from "../../components/login-messages-dialog/LoginMessagesDialog";
import MobileHierarchy from "../../components/mobile-hierarchy/MobileHierarchy";
import MobileUserFavorites from "../../components/mobile-user-favorites/MobileUserFavorites";
import { NavDrawerAction } from '../../components/nav-drawer/NavDrawerAction';
import UserFavoritesDrawer from "../../components/user-favorites-drawer/UserFavoritesDrawer";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { OfflineDetectActionType } from "../../contexts/offline-detect-context/OfflineDetectContextTypes";
import { UserStateActionTypeEnum, useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { IRoute, routes, routesByViewID, UIViewCategory } from "../../routes/routes";
import eLynxAuthService from "../../services/authentication/elynx-auth-service/ELynxAuthService";
import msalAuthService from "../../services/authentication/msal-auth-service/MsalAuthService";
import { StorageKeys } from "../../services/storage/StorageKeys";
import storageService from "../../services/storage/storage-service/StorageService";
import UserDataService from "../../services/user-data-service/UserDataService";
import { LoginChangeCustomerStorageKey, RedirectFromLogoutStorageKey } from "../../views/login-view/LoginViewUtils";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import AccountSettings from "../../components/account-settings/AccountSettings";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { useBreadcrumb } from "../../queries/breadcrumb/UseBreadcrumb";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { LayoutContextActionType, SwipeActionEnum } from "../../contexts/layout-context/LayoutContextTypes";
import { useLayoutContext } from "../../contexts/layout-context/UseLayoutContext";
import { FeatureConstants } from "../../services/features-data-service/Models";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import NavDrawer from "../../components/nav-drawer/NavDrawer";
import NavDrawerV2 from "../../components/nav-drawer/NavDrawerV2";
import { useUIEffectiveCustomizationSettings } from "../../queries/ui-customization/UseEffectiveUICustomizationSettings";
import ContextualNavDrawer from "../../components/contextual-nav-drawer/ContextualNavDrawer";

export default function DefaultLayout() {

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopHierarchyOpen, setDesktopHierarchyOpen] = useState(false);
  const [mobileHierarchyOpen, setMobileHierarchyOpen] = useState(false);
  const [desktopFavoritesOpen, setDesktopFavoritesOpen] = useState(false);
  const [mobileFavoritesOpen, setMobileFavoritesOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const navigateTo = useNavigateTo();
  const theme = useTheme();
  const isPhone = isMobile && !isTablet;
  const [accountOpen, setAccountOpen] = useState(false);
  const [userState, dispatchUserState] = useUserContext();
  const [appSettings] = useAppSettings();
  const [offlineState, dispatchOfflineState] = useOfflineDetectContext();
  const { t } = useTranslation();
  const [, setAppNotificationState] = useAppNotificationContext();
  const routeContext = useRouteContext();
  const location = useLocation();
  const [/*contentLayoutState,*/, layoutDispatch] = useLayoutContext();
  const [dawerPinned, setDrawerPinned] = useState(false);
  const [contextualNavDrawerOpen, setContextualNavDrawerOpen] = useState(false);

  const viewID = getParentViewIDFromPath(location.pathname, UIViewEnum.Group_SummaryListing_Devices);


  const { isLandscape } = useMobileOrientation();


  const { data: breadCrumbData } = useQueryNotifications(useBreadcrumb)({
    customerID: userState.customer?.id as number,
    viewID: viewID,
    routeContext: routeContext,
    offlineDataLoaded: offlineState.offlineDataLoaded,
    offline: offlineState.offline
  }
  );

  const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature<boolean>)({
    feature: FeatureConstants.NavigationV2,
  });

  const navigationV2 = (navigationV2Feature ?? false);

  const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
    feature: FeatureConstants.HeaderAndActionsV2,
  });

  const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);

  const hideAppBar = headerAndActionsV2 && isPhone && isLandscape;

  const {
    data: customizationSettings
  } = useQueryNotifications(useUIEffectiveCustomizationSettings)({
    userID: userState.user?.id as number,
    enabled: navigationV2
  });

  const openOnHover = customizationSettings?.navigationDrawer.openOnHover;
  const hasRelatedItems = (breadCrumbData?.relatedItems?.length ?? 0) > 1;
  const navigationItem = breadCrumbData?.breadcrumbItems?.[breadCrumbData.breadcrumbItems.length - 1];


  const route = routes.find(o => o.fullPath === location.pathname);
  const adminView = route?.category === UIViewCategory.AdminGroup || route?.category === UIViewCategory.AdminView;

  // console.log("RENDER - Default Layout");
  const onCloseHierarchy = () => {
    setDesktopHierarchyOpen(false);
  };

  const onCloseFavorites = () => {
    setDesktopFavoritesOpen(false);
  };

  const onToggleNavDrawer = () => {
    setDrawerOpen(open => !open);
  };


  function onNavDrawerAction(action: NavDrawerAction) {
    switch (action) {

      case NavDrawerAction.Administration: {
        const url = getRouteFromContext(UIViewEnum.Administration, routeContext);
        navigateTo(url);
        setDrawerOpen(false);
      }
        break;

      case NavDrawerAction.ToggleHierarchy:
        toggleHierarchy();
        break;

      case NavDrawerAction.ToggleFavorites:
        toggleFavorites();
        break;

      case NavDrawerAction.ShowAccount:
        showAccount();
        break;

      case NavDrawerAction.ChangeCustomer:
        changeCustomer();
        break;

      case NavDrawerAction.ShowHelp:
        showHelp();
        break;

      case NavDrawerAction.SignOut:
        signOut();
        break;

      case NavDrawerAction.Offline: {

        const forceOffline = !offlineState.forceOffline;

        dispatchOfflineState({
          type: OfflineDetectActionType.ForceOfflineChanged,
          payload: forceOffline
        });

        if (forceOffline && !offlineState.offline) {

          if (forceOffline) {
            setAppNotificationState({
              notificationType: AppNotificationTypeEnum.Info,
              message: t("offlineDetect.isOffline"),
              hideContactInfo: true,
              show: true
            });
          }
        }
      }

        break;

      case NavDrawerAction.ToggleContextualNavDrawer:
        toggleContextualNavDrawer();
        break;
    }
  }

  function toggleHierarchy() {
    if (!isPhone) {
      setDesktopFavoritesOpen(false);
      setDesktopHierarchyOpen(treeOpen => !treeOpen);
      setContextualNavDrawerOpen(false);
    } else {
      setMobileHierarchyOpen(true);
    }
  }

  function toggleFavorites() {
    if (!isPhone) {
      setDesktopHierarchyOpen(false);
      setDesktopFavoritesOpen(open => !open);
      setContextualNavDrawerOpen(false);
    } else {
      setMobileFavoritesOpen(true);
    }
  }

  function toggleContextualNavDrawer() {
    setDesktopHierarchyOpen(false);
    setDesktopFavoritesOpen(false);
    setContextualNavDrawerOpen(open => !open);
  }

  function showAccount() {
    setAccountOpen(true);
  }

  function changeCustomer() {

    storageService.session.set(LoginChangeCustomerStorageKey, true);

    const url = `${routesByViewID.get(UIViewEnum.Login)?.fullPath}`;
    navigateTo(url, { replace: true });
  }

  function showHelp() {
    setHelpOpen(true);
  }

  function onPreviousNext(direction: SwipeActionEnum) {
    layoutDispatch({
      type: LayoutContextActionType.SwipeChanged,
      payload: direction
    });
  }

  function signOut() {

    eLynxAuthService.logout();

    const isSSO = UserDataService.getIsSSO();
    if (isSSO) {
      msalAuthService.signOut();
    }

    //else {
    storageService.session.set(RedirectFromLogoutStorageKey, true);
    //}
    storageService.local.remove(StorageKeys.IsSSO);

    dispatchUserState({
      type: UserStateActionTypeEnum.ResetUserState,
    });

    setTimeout(() => {

      storageService.local.remove(StorageKeys.LastCustomerID);
      storageService.session.remove(StorageKeys.LastCustomerID);
      storageService.local.remove(StorageKeys.RedirectRestorePath);

      navigateTo(routesByViewID.get(UIViewEnum.Login)?.fullPath as string, { replace: true });
    }, 500);


  };



  useEffect(() => {
    if (openOnHover) {
      setDrawerOpen(false);
    }
  }, [openOnHover]);


  //const [navigationItem, setNavigationItem] = useState<INavigationItem|undefined>(undefined);


  //padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
  //height: "100vh",
  return (

    <Box sx={{
      display: "flex",
      height: isMobileSafari && navigator.maxTouchPoints > 1 ? "-webkit-fill-available" : "100vh",
      overflow: "hidden",
      position: 'relative',
      flexDirection: isPhone ? 'column' : 'row',

    }}>

      {/* <ActionsContextProvider> */}
      {!isPhone &&
        <AppHeader hideMenu={isPhone || openOnHover} onToggleDrawer={() => setDrawerOpen(open => !open)} />
      }

      {navigationV2
        ? <NavDrawerV2
          open={drawerOpen}
          customizationSettings={customizationSettings}
          onAction={onNavDrawerAction}
          onOpen={() => {
            setDrawerOpen(true);
          }}
          onClose={() => {
            setDrawerOpen(false);
          }}
          contextualNavDrawerOpen={contextualNavDrawerOpen}
        />
        : <NavDrawer
          open={drawerOpen}
          customizationSettings={customizationSettings}
          onAction={onNavDrawerAction}
          onOpen={() => {
            setDrawerOpen(true);
          }}
          onClose={() => {
            setDrawerOpen(false);
          }}
        />
      }

      {/* This is the desktop version of the hierarchy */}
      <Box style={{ display: desktopHierarchyOpen ? "" : "none" }}>
        <HierarchyDrawer
          open={desktopHierarchyOpen}
          onClose={() => onCloseHierarchy()}
          pinned={dawerPinned}
          onPinClick={() => setDrawerPinned(p => !p)}
        />
      </Box>

      {/* This is the desktop version of the favorites */}
      <Box style={{ display: desktopFavoritesOpen ? "" : "none" }}>
        <UserFavoritesDrawer
          open={desktopFavoritesOpen}
          onClose={() => onCloseFavorites()}
          pinned={dawerPinned}
          onPinClick={() => setDrawerPinned(p => !p)}
        />
      </Box>


      {/* should be able to get navigation item from breadcrumb data, if not just pass  */}
      {!isPhone &&
        <Box style={{ display: contextualNavDrawerOpen ? "" : "none" }}>
          <ContextualNavDrawer
            open={contextualNavDrawerOpen}
            onClose={() => setContextualNavDrawerOpen(false)}
            item={navigationItem!}
            route={route!}
            pinned={dawerPinned}
            onPinClick={() => setDrawerPinned(p => !p)}
          />
        </Box>
      }

      {/* Main Panel */}
      <Box id='mainpnl' sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        overflow: "hidden",
        //flexBasis: 'auto',
        flexBasis: '100%',
        marginBottom: isPhone ? theme.spacing(7) : undefined,
        position: 'relative',

      }}>

        {!isPhone &&
          <>
            <Box sx={{
              minHeight: !headerAndActionsV2 && appSettings.maximizeContent ? 52 : 56,
              // [`${theme.breakpoints.up('xs')} and (orientation: landscape)`]: {
              //   minHeight: 48,
              // },
              [theme.breakpoints.up('sm')]: {
                minHeight: !headerAndActionsV2 && appSettings.maximizeContent ? 52 : 64,
              },
            }} />


            {!adminView && hasRelatedItems &&
              <>
                <Fab
                  sx={{
                    position: "absolute",
                    top: '50%',
                    left: theme.spacing(1),
                    opacity: 0.3,
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                  onClick={(() => onPreviousNext(SwipeActionEnum.Right))}
                >
                  <NavigateBefore />
                </Fab>

                <Fab
                  sx={{
                    position: "absolute",
                    top: '50%',
                    right: theme.spacing(2),
                    opacity: 0.3,
                    '&:hover': {
                      opacity: 1
                    }

                  }}
                  onClick={(() => onPreviousNext(SwipeActionEnum.Left))}
                >
                  <NavigateNext />
                </Fab>

              </>
            }



          </>
        }

        <Outlet />

        {/* render bottom navigation on functions on small form factor (mobile phone) devices */}
        {
          isPhone &&
          <>

            {/* This is the mobile version of the hierarchy */}
            <MobileHierarchy open={mobileHierarchyOpen} onClose={() => {
              setDrawerOpen(false);
              setMobileHierarchyOpen(false);
            }} />

            {/* This is the mobile version of the favorites */}
            <MobileUserFavorites open={mobileFavoritesOpen} onClose={() => {
              setDrawerOpen(false);
              setMobileFavoritesOpen(false);
            }} />

            {
              !hideAppBar &&
              <AppBarBottom onToggleNavDrawer={onToggleNavDrawer} />
            }

          </>
        }

        {/* <AccountSettingsContextProvider> */}
        <AccountSettings open={accountOpen} onClose={() => setAccountOpen(false)} />
        {/* </AccountSettingsContextProvider> */}

      </Box>

      <HelpMenu
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      {!offlineState.offline &&
        <LoginMessagesDialog />
      }

    </Box >
  );
}